import type { JwtToken } from "../../cbi-generator/generated/dto/JwtToken";
import { JwtType } from "../../cbi-generator/generated/dto/JwtType";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return (
    isRecord(value) &&
    Object.values(value).every((entry) => typeof entry === "string")
  );
}

// Decoding is only for client state; authentication is verified by Compass.
export function decodeAuthToken(token: string): JwtToken {
  const parts = token.split(".");
  if (parts.length !== 3) throw new Error("Invalid JWT format");
  const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const bytes = Uint8Array.from(
    atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")),
    (char) => char.charCodeAt(0),
  );
  const payload: unknown = JSON.parse(new TextDecoder().decode(bytes));
  if (
    !isRecord(payload) ||
    typeof payload.exp !== "number" ||
    typeof payload.iat !== "number"
  ) {
    throw new Error("Invalid JWT timestamps");
  }
  const expiration = new Date(payload.exp * 1000);
  const issuedAt = new Date(payload.iat * 1000);
  const audience = Array.isArray(payload.aud) ? payload.aud[0] : payload.aud;
  if (
    !Number.isFinite(expiration.getTime()) ||
    !Number.isFinite(issuedAt.getTime()) ||
    typeof audience !== "string" ||
    (payload.type !== "access" && payload.type !== "refresh") ||
    !isStringRecord(payload.permissions)
  ) {
    throw new Error("Invalid JWT payload");
  }
  const claims: Record<string, string> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (typeof value === "string") claims[key] = value;
  }
  return {
    token,
    audience,
    expiration,
    issuedAt,
    claims,
    permissions: payload.permissions,
    type: payload.type === "refresh" ? JwtType.REFRESH : JwtType.ACCESS,
  };
}

export function restoreAuthTokens(serialized: string | null): {
  accessToken: JwtToken | null;
  refreshToken: JwtToken | null;
} {
  if (!serialized || serialized === "undefined")
    return { accessToken: null, refreshToken: null };
  const state: unknown = JSON.parse(serialized);
  if (!isRecord(state))
    throw new Error("Invalid persisted authentication state");
  const restore = (value: unknown, expected: JwtType): JwtToken | null => {
    if (value == null) return null;
    if (!isRecord(value) || typeof value.token !== "string")
      throw new Error("Invalid persisted token");
    const token = decodeAuthToken(value.token);
    if (token.type !== expected)
      throw new Error("Unexpected persisted token type");
    return token;
  };
  return {
    accessToken: restore(state.accessToken, JwtType.ACCESS),
    refreshToken: restore(state.refreshToken, JwtType.REFRESH),
  };
}

export function createSingleFlight<T>() {
  let pending: Promise<T> | null = null;
  return (action: () => Promise<T>): Promise<T> => {
    if (!pending) {
      pending = Promise.resolve()
        .then(action)
        .finally(() => {
          pending = null;
        });
    }
    return pending;
  };
}

// Compass's JWT challenge body; other 401s are permission/ownership failures.
export const INVALID_SESSION_MESSAGE = "Token expired or invalid";

export function isInvalidSessionResponse(
  status: number | undefined,
  data: unknown,
) {
  return (
    status === 401 &&
    typeof data === "string" &&
    data.trim() === INVALID_SESSION_MESSAGE
  );
}

// Network failures and server errors must not end the session.
export function isRefreshRejected(status: number | undefined) {
  return status === 400 || status === 401 || status === 403;
}
